import { css } from "@emotion/react";
import type { PropsWithChildren, ReactNode } from "react";
import { useMemo } from "react";
import { Button, Pressable } from "react-aria-components";
import { Link, useLocation } from "react-router";

import {
  Flex,
  Icon,
  Icons,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  PopoverArrow,
  Text,
  Tooltip,
  TooltipTrigger,
} from "@phoenix/components";
import { GitHubStarCount } from "@phoenix/components/nav/GitHubStarCount";
import { isProviderThemeMode, useTheme, useViewer } from "@phoenix/contexts";
import { assertUnreachable } from "@phoenix/typeUtils";

import { Logo, LogoText } from "./Logo";

const topNavCSS = css`
  padding: var(--ac-global-dimension-static-size-100);
  background-color: var(--ac-premium-glass-bg);
  backdrop-filter: var(--ac-premium-glass-blur);
  border-bottom: 1px solid var(--ac-premium-glass-border);
  flex: none;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: var(--global-dimension-static-size-100);

  .copy-action-menu__button {
    opacity: 0;
    transition: none;
  }
  &:hover .copy-action-menu__button,
  .copy-action-menu__button[data-pressed],
  .copy-action-menu__button[data-copied] {
    opacity: 1;
    transition: opacity 0.15s ease-in-out;
  }
`;

const sideNavCSS = css`
  padding: 12px 8px;
  background-color: var(--ac-premium-glass-bg);
  backdrop-filter: var(--ac-premium-glass-blur);
  border-right: 1px solid var(--ac-premium-glass-border);
  flex: none;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100vh;
  max-height: 100vh;
  width: var(--nav-collapsed-width);
  transition: width 0.15s cubic-bezier(0, 0.57, 0.21, 0.99);
  overflow: hidden;

  &[data-expanded="true"] {
    width: var(--nav-expanded-width);
  }
  &[data-expanded="false"] {
    padding: 12px 0;
    .brand-text-wrap {
      opacity: 0;
      display: none;
    }
  }
`;

const navLinkCSS = css`
  width: auto;
  min-height: 30px;
  height: 30px;
  box-sizing: border-box;
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #1e293b;
  background-color: transparent;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 10px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s ease;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.88);
  }

  .icon-wrap {
    padding: 0;
    margin-left: 0;
    margin-right: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    color: #475569;

    .theme--dark & {
      color: inherit;
    }

    .material-symbols-outlined {
      font-size: 15px;
      font-variation-settings: 'FILL' 0, 'wght' 500;
      line-height: 1;
      transition: font-variation-settings 0.15s ease;
    }

    svg {
      width: 15px;
      height: 15px;
    }
  }

  .text {
    padding: 0;
    white-space: nowrap;
    flex: 1;
    text-align: start;
    font-size: 13px !important;
    font-weight: 500 !important;
    line-height: 18px !important;
    color: #1e293b !important;
    display: flex;
    align-items: center;

    .theme--dark & {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  }

  .counter {
    font-size: 11px !important;
    font-weight: 500 !important;
    color: #64748b !important;
    background-color: #f1f5f9;
    border: none;
    border-radius: 9999px;
    padding: 0 6px;
    height: 18px;
    min-width: 18px;
    line-height: 18px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;

    .theme--dark & {
      color: rgba(255, 255, 255, 0.6) !important;
      background-color: rgba(255, 255, 255, 0.08);
    }
  }

  &.active {
    color: #004d47;
    background-color: #e6f4f1;
    border-radius: 8px;

    .theme--dark & {
      color: #14bab6;
      background-color: rgba(20, 186, 182, 0.15);
    }

    .icon-wrap {
      color: #004d47;
      .theme--dark & {
        color: #14bab6;
      }
      .material-symbols-outlined {
        font-variation-settings: 'FILL' 1, 'wght' 600;
      }
      svg {
        stroke-width: 2px;
        fill: currentColor;
      }
    }

    .text {
      color: #004d47 !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      line-height: 18px !important;
      .theme--dark & {
        color: #14bab6 !important;
      }
    }

    .counter {
      background-color: #ffffff !important;
      color: #004d47 !important;
      font-weight: 600 !important;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      .theme--dark & {
        background-color: rgba(20, 186, 182, 0.3) !important;
        color: #ffffff !important;
      }
    }
  }

  &:hover:not(.active) {
    color: #0f172a;
    background-color: #f1f5f9;

    .icon-wrap {
      color: #0f172a;
    }

    .text {
      color: #0f172a !important;
    }

    .theme--dark & {
      color: #ffffff;
      background-color: rgba(255, 255, 255, 0.08);

      .icon-wrap,
      .text {
        color: #ffffff !important;
      }
    }
  }

  nav[data-expanded="false"] & {
    justify-content: center;
    min-height: 32px;
    height: 32px;
    margin: 0;
    padding: 0;
    border-radius: 8px;

    &.active {
      background-color: #e6f4f1;
      border-radius: 8px;

      .theme--dark & {
        background-color: rgba(20, 186, 182, 0.15);
      }
    }

    .icon-wrap {
      margin-left: 0;
      margin-right: 0;
    }
    .text {
      display: none;
    }
    .counter {
      display: none;
    }
  }
`;

const brandCSS = css`
  color: var(--global-text-color-900);
  font-size: var(--global-font-size-xl);
  text-decoration: none;
  margin: 0 0 var(--global-dimension-static-size-200) 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--global-dimension-static-size-150);
  overflow: hidden;
  width: 100%;
  & > * {
    flex: none;
  }
  .brand-text-wrap {
    opacity: 1;
    transition: all 0.8s ease-in-out;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  nav[data-expanded="false"] & {
    justify-content: center;
    margin: 0 0 var(--global-dimension-static-size-200) 0;

    .brand-text-wrap {
      display: none;
    }
  }
`;

function ExternalLink(props: {
  href: string;
  leadingVisual: ReactNode;
  trailingVisual?: ReactNode;
  text: string;
  replaceTab?: boolean;
  isExpanded: boolean;
}) {
  return (
    <TooltipTrigger delay={0} isDisabled={props.isExpanded}>
      <Pressable>
        <a
          href={props.href}
          target={props.replaceTab ? undefined : "_blank"}
          css={navLinkCSS}
          rel="noreferrer"
          role="button"
        >
          {props.leadingVisual}
          <Text>{props.text}</Text>
          {props.trailingVisual}
        </a>
      </Pressable>
      <Tooltip placement="right" offset={10}>
        {props.text}
      </Tooltip>
    </TooltipTrigger>
  );
}

export function DocsLink({ isExpanded }: { isExpanded: boolean }) {
  return (
    <ExternalLink
      href="https://arize.com/docs/phoenix"
      leadingVisual={<Icon svg={<Icons.BookOutline />} />}
      text="Documentation"
      isExpanded={isExpanded}
    />
  );
}

export function GitHubLink({ isExpanded }: { isExpanded: boolean }) {
  return (
    <ExternalLink
      href="https://github.com/Arize-ai/phoenix"
      leadingVisual={<Icon svg={<Icons.GitHub />} />}
      trailingVisual={<GitHubStarCount />}
      text="Star on GitHub"
      isExpanded={isExpanded}
    />
  );
}

export function ThemeSelector({ isExpanded }: { isExpanded: boolean }) {
  const { theme, systemTheme, themeMode, setThemeMode } = useTheme();
  const { themeText, themeIcon } = useMemo(() => {
    let themeIcon: ReactNode;
    let themeText: string;
    switch (themeMode) {
      case "light":
        themeIcon = <Icons.SunOutline />;
        themeText = "Light";
        break;
      case "dark":
        themeIcon = <Icons.MoonOutline />;
        themeText = "Dark";
        break;
      case "system":
        themeIcon = <Icons.HalfMoonHalfSunOutline />;
        themeText = `${theme === "light" ? "Light" : "Dark"} (auto)`;
        break;
      default:
        assertUnreachable(themeMode);
    }
    return {
      themeIcon,
      themeText,
    };
  }, [theme, themeMode]);

  return (
    <TooltipTrigger delay={0} isDisabled={isExpanded}>
      <MenuTrigger>
        <Button css={navLinkCSS} className="button--reset">
          <Icon svg={themeIcon} />
          <Text>{themeText}</Text>
        </Button>
        <Popover placement="right top">
          <PopoverArrow />
          <Menu
            aria-label="Theme selection"
            selectedKeys={new Set([themeMode])}
            selectionMode="single"
            onSelectionChange={(keys) => {
              const selectedKey =
                keys instanceof Set ? Array.from(keys)[0] : null;
              if (selectedKey && isProviderThemeMode(selectedKey)) {
                setThemeMode(selectedKey);
              }
            }}
          >
            <MenuItem id="system">
              <Flex
                direction="row"
                gap="size-100"
                justifyContent="start"
                alignItems="center"
              >
                <Icon svg={<Icons.HalfMoonHalfSunOutline />} />
                <Text>{`Auto (${systemTheme})`}</Text>
              </Flex>
            </MenuItem>
            <MenuItem id="dark">
              <Flex
                direction="row"
                gap="size-100"
                justifyContent="start"
                alignItems="center"
              >
                <Icon svg={<Icons.MoonOutline />} />
                <Text>Dark</Text>
              </Flex>
            </MenuItem>
            <MenuItem id="light">
              <Flex
                direction="row"
                gap="size-100"
                justifyContent="start"
                alignItems="center"
              >
                <Icon svg={<Icons.SunOutline />} />
                <Text>Light</Text>
              </Flex>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>
      <Tooltip placement="right" offset={10}>
        {themeText}
      </Tooltip>
    </TooltipTrigger>
  );
}

export function Brand() {
  return (
    <Link
      to="/"
      css={brandCSS}
      title={`version: ${window.Config.platformVersion}`}
    >
      <Logo />
      <div className="brand-text-wrap">
        <LogoText className="brand-text" />
      </div>
    </Link>
  );
}

export function TopNavbar({ children }: { children: ReactNode }) {
  return <nav css={topNavCSS}>{children}</nav>;
}

export function SideNavbar({
  children,
  isExpanded,
  ...props
}: PropsWithChildren<{ isExpanded: boolean } & React.HTMLAttributes<HTMLElement>>) {
  return (
    <nav data-expanded={isExpanded} css={sideNavCSS} {...props}>
      {children}
    </nav>
  );
}

export function NavLink(props: {
  to: string;
  text: string;
  leadingVisual: ReactNode;
  trailingVisual?: ReactNode;
  isExpanded: boolean;
  isActive?: (isActive: boolean, location: ReturnType<typeof useLocation>) => boolean;
}) {
  const location = useLocation();
  const defaultActive = location.pathname === props.to || (location.pathname.startsWith(props.to + "/") && props.to !== "/");
  const active = props.isActive ? props.isActive(defaultActive, location) : defaultActive;
  return (
    <TooltipTrigger delay={0} isDisabled={props.isExpanded}>
      <Pressable>
        <div role="button">
          <Link
            to={props.to}
            css={navLinkCSS}
            className={active ? "active" : ""}
          >
            <div className="icon-wrap">{props.leadingVisual}</div>
            <Text className="text">{props.text}</Text>
            {props.trailingVisual}
          </Link>
        </div>
      </Pressable>
      <Tooltip placement="right" offset={10}>
        {props.text}
      </Tooltip>
    </TooltipTrigger>
  );
}

export function NavButton(props: {
  text: string;
  leadingVisual: ReactNode;
  onClick: () => void;
}) {
  return (
    <button className="button--reset" css={navLinkCSS} onClick={props.onClick}>
      {props.leadingVisual}
      <Text>{props.text}</Text>
    </button>
  );
}

export const ManagementLink = ({ isExpanded }: { isExpanded: boolean }) => {
  const { viewer } = useViewer();

  if (viewer?.isManagementUser && window.Config.managementUrl) {
    return (
      <li key="management">
        <ExternalLink
          href={window.Config.managementUrl}
          leadingVisual={<Icon svg={<Icons.Server />} />}
          text="Management Console"
          replaceTab
          isExpanded={isExpanded}
        />
      </li>
    );
  }
  return null;
};
